import { useEffect, useState } from 'react';
import { message, Table, Button, Card, Space, Row, Col, Input, Typography, Collapse, Divider, Switch, Tag, Descriptions } from 'antd';
import { tablePageSize } from '../../../utils/constants';
import { CheckOutlined, CloseOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import NewUser from './newUser';
import UpdateUser from './updateUser';
import "./users.scss";
import UsersServices from '../../../services/usersServices';
import { usePageTitle } from '../../../context/pageTitleContext';
import { useTranslation } from 'react-i18next';
import { useUserAuth } from '../../../context/UserContext';
import { User, UserCountries, UserFilter } from './users.types';
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getCountryLabel } from '../../../utils/functions';
import { Can, permissions } from '../../../utils/permissionUtils';

const { Link } = Typography;

const initialFilters: UserFilter = {
    name: '',
    surname: '',
    email: '',
    enabled: true
};

const UsersList = () => {
    const { t } = useTranslation();
    const { setPageTitle } = usePageTitle();
    const queryClient = useQueryClient();
    const [openCreateDrawer, setOpenCreateDrawer] = useState(false);
    const [openUpdateDrawer, setOpenUpdateDrawer] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<User | null>(null);
    const [onFilterActive, setOnFilterActive] = useState(false);
    const [filters, setFilters] = useState(initialFilters);
    const [appliedFilters, setAppliedFilters] = useState(initialFilters);
    const { user } = useUserAuth();

    useEffect(() => {
        setPageTitle(t('page.users'));
    }, []);

    const refreshUsers = () => {
        queryClient.invalidateQueries({
            queryKey: ["users"]
        });
    };

    const {
        data: users = [] as User[],
        isLoading: loadingData,
        isError,
    } = useQuery({
        queryKey: ["users", appliedFilters, user.id],

        queryFn: async () => {
            const request = {
                ...appliedFilters,
                userID: user.id,
            };

            const respData = await UsersServices.getUsers(request);

            if (!respData.success) {
                throw new Error("Failed to load users");
            }

            return respData.responseObject.value.usersList;
        },
    });

    useEffect(() => {
        if (isError) {
            message.error(t('messages.errorAccured'));
        }
    }, [isError]);
    


    const showDrawer = (drawerName: string, record: User | null) => {
        if (drawerName === "create") setOpenCreateDrawer(true);
        if (drawerName === "update") { setOpenUpdateDrawer(true); setSelectedRecord(record); }
    }

    const onCloseDrawer = (drawerName: string) => {
        if (drawerName === "create") setOpenCreateDrawer(false);
        if (drawerName === "update") setOpenUpdateDrawer(false);
    };

    const onFilter = () => {
        setAppliedFilters(filters);
    };

    const onClearFilters = () => {
        setOnFilterActive(false);
        setFilters(initialFilters);
        setAppliedFilters(initialFilters);
    }

    const handelFilterChange = (name: string, value: any) => {
        setFilters((prev) => ({ ...prev, [name]: value }));
        setOnFilterActive(true);
    };

    const columns = [
        {
            title: t('users.name'),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: t('users.lastname'),
            dataIndex: 'surname',
            key: 'surname',
        },
        {
            title: t('users.username'),
            dataIndex: 'username',
            key: 'username',
        },
        {
            title: t('users.email'),
            dataIndex: 'email',
            key: 'email'
        },
        {
            title: t('users.role'),
            dataIndex: "roleName",
            key: 'roleName',
        },
        {
            title: t('users.country'),
            dataIndex: "countries",
            key: 'countries',
            render: (_: any, record: User) => {
                const countries = record.countries?.map((country: UserCountries) => {
                    return {
                        countryID: country.countryID,
                        code: country.countryCode,
                        description: country.countryDescription
                    }
                });
                return (
                    <>
                        <Space direction='horizontal' style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
                            {countries?.map((country: any) => (
                                <Tag key={country.countryID}> {getCountryLabel(country)}</Tag>
                            ))}
                        </Space>
                    </>
                )
            }
        },
        {
            title: t('users.active'),
            dataIndex: "enabled",
            key: 'enabled',
            render: (_: any, record: User) => {
                return (
                    <>
                        {record.enabled && (
                            <CheckOutlined style={{ color: "#81cf08" }} />
                        )}

                        {!record.enabled && (
                            <CloseOutlined style={{ color: "#ee6c7c" }} />
                        )}
                    </>
                )
            }
        },
        {
            key: "action",
            width: "50px",
            render: (_: any, record: User) => {
                return (
                    <Can permission={permissions.UPDATE_USER}>
                    <Space>
                        <Link onClick={() => showDrawer("update", record)}><EditOutlined className='editIcon' /></Link >
                    </Space>
                    </Can>
                )
            }
        }
    ];

    const filterItems = [
        {
            key: '1',
            label: t('general.filters'),
            children: (
                <>
                    <Row gutter={16}>
                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 8 }} lg={{ span: 4 }} >
                            <Space direction='vertical'>
                                <span>{t('users.name')}</span>
                                <Input
                                    value={filters.name}
                                    onChange={(e) => handelFilterChange("name", e.target.value)}
                                />
                            </Space>
                        </Col>

                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 8 }} lg={{ span: 4 }} >
                            <Space direction='vertical'>
                                <span>{t('users.lastname')}</span>
                                <Input
                                    value={filters.surname}
                                    onChange={(e) => handelFilterChange("surname", e.target.value)}
                                />
                            </Space>
                        </Col>

                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 8 }} lg={{ span: 4 }} >
                            <Space direction='vertical'>
                                <span>{t('users.email')}</span>
                                <Input
                                    value={filters.email}
                                    onChange={(e) => handelFilterChange("email", e.target.value)}
                                />
                            </Space>
                        </Col>

                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 8 }} lg={{ span: 4 }} >
                            <Space direction='vertical'>
                                <span>{t('users.active')}</span>
                                <Switch checked={filters.enabled} onChange={(checked) => handelFilterChange("enabled", checked)} />
                            </Space>
                        </Col>
                    </Row>

                    <Divider />
                    <Row gutter={{ lg: 24 }}>
                        <Col className="gutter-row flexAlignEnd" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Button disabled={!onFilterActive} type="primary" style={{ marginRight: 10 }} onClick={() => onFilter()}> {t('general.filter')} </Button>
                            <Button disabled={!onFilterActive} onClick={() => onClearFilters()}> {t('general.clear')} </Button>
                        </Col>
                    </Row>
                </>
            )
        }
    ]

    const onClose = () => {
        onCloseDrawer("update");
        setSelectedRecord(null);
    }

      return (
        <div className='usersList'>
            <NewUser
                open={openCreateDrawer}
                onClose={() => onCloseDrawer("create")}
            />

            {selectedRecord && (
                <UpdateUser
                    open={openUpdateDrawer}
                    onClose={() => onClose()}
                    // getUsers={refreshUsers}
                    recordData={selectedRecord}
                />
            )}

            <Row gutter={{ lg: 24 }}>
                <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                    <Collapse size='small' className='filterPanel' items={filterItems} defaultActiveKey={['1']} />
                </Col>
            </Row>

            <Divider />

            <Card size='small' extra={<Button size='small' type="default" onClick={() => showDrawer("create", null)} icon={<PlusOutlined />}>{t('users.addNewUser')}</Button>}>
                <Table
                    dataSource={users}
                    columns={columns}
                    rowKey={obj => obj.id}
                    pagination={{
                        pageSize: tablePageSize
                    }}
                    loading={loadingData}
                    size='small'
                    bordered
                />
            </Card>
        </div>
    )
}
export default UsersList;